import { Component, OnInit, Input, forwardRef, ViewEncapsulation, Output, EventEmitter, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

import { ResourceWithValidations } from '../../modules/recursos/services/recursos.service';
import { NewResourceComponent } from '../new-resource/new-resource.component';
import { LocalStorageService } from '../../services/local-storage.service';

@Component({
	selector: 'app-resource-autocomplete',
	templateUrl: './resource-autocomplete.component.html',
	styleUrls: ['./resource-autocomplete.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => ResourceAutocompleteComponent),
			multi: true,
		},
	],
	encapsulation: ViewEncapsulation.None
})
export class ResourceAutocompleteComponent implements OnInit, OnChanges, ControlValueAccessor {
	@Input() group: FormGroup | undefined;
	@Input() formControlName: string = 'custom-select';
	@Input() resourcesList: ResourceWithValidations[] = [];
	@Output() resourcesListChange = new EventEmitter<ResourceWithValidations[]>();
	@Input() allowResourceCreation?: boolean = false;
	@Input() allowResourceEdition?: boolean = false;

	// Form configuration
	onChange?: (value: ResourceWithValidations) => void;
	onTouched?: (value: boolean) => void;
	formControl = new FormControl<ResourceWithValidations | null>(null);
	value: ResourceWithValidations | null = null;
	valueWrited: string = '';
	filteredOptions: ResourceWithValidations[] = [];
	dialogSubscription = new Subscription();

	constructor(private dialog: MatDialog, private ls: LocalStorageService, private cdr: ChangeDetectorRef) { }

	ngOnInit(): void {
		this.formControl.valueChanges.subscribe(() => this.updateResourcesList());
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['resourcesList'] && this.resourcesList.length > 0) {
			this.updateResourcesList();
		}
	}

	getOptionText(option: ResourceWithValidations) {
		return option?.name ?? '';
	}

	onWriteResource(event: Event){
		const target = event.target as HTMLInputElement;
		this.valueWrited = target.value ?? '';
	}

	resourceSelected(event: MatAutocompleteSelectedEvent) {
		const selected = event.option.value as ResourceWithValidations;
		const searchedValue = this.valueWrited;

		if (selected.id === 'addNew') {
			this.formControl.reset();

			const dialogRef = this.dialog.open(NewResourceComponent, {
				...NewResourceComponent.defaultConfig,
				data: {
					defaultValue: searchedValue
				}
			});

			this.dialogSubscription = dialogRef.afterClosed()
				.subscribe((newResource: ResourceWithValidations | null | undefined) => {
					if (newResource) {
						this.resourcesList.push({
							...newResource,
							inventaries: false,
							resourceReservations: false,
							projectResources: false,
						});

						this.writeValue(this.resourcesList[this.resourcesList.length - 1]);
					}
				});
		} else {
			this.writeValue(selected);
		}
	}

	registerOnChange(onChange: (value: ResourceWithValidations) => void): void {
		this.onChange = onChange;
	}

	registerOnTouched(onTouched: (value: boolean) => void): void {
		this.onTouched = onTouched;
	}

	writeValue(value: ResourceWithValidations) {
		this.value = value;
		this.valueWrited = value?.name ?? '';
		this.formControl.setValue(value);

		if (this.onChange) {
			this.onChange(this.value);
		}
		if (this.onTouched) {
			this.onTouched(true);
		}
	}

	isEditEnabled(value: ResourceWithValidations){
		if (!this.allowResourceEdition) {
			return false;
		}
		// Se permite la edición si el recurso fue creado por el usuario y no se utiliza.
		const hasRelations = value?.inventaries 
			|| value?.resourceReservations
			|| value?.projectResources;
		const userId = this.ls.getItem('userId');

		return value.created_by?.id === userId && !hasRelations;
	}

	editResource(event: MouseEvent, value: ResourceWithValidations){
		event.stopPropagation();

		const dialogRef = this.dialog.open(NewResourceComponent, {
			...NewResourceComponent.defaultConfig,
			data: {
				resourceToEdit: value
			}
		});

		this.dialogSubscription = dialogRef.afterClosed()
			.subscribe((editedResource: ResourceWithValidations | null | undefined) => {
				if (editedResource) {
					const findIndex = this.resourcesList.findIndex(r => r.id === editedResource.id);
					const newList = [...this.resourcesList];
					if (findIndex > -1) {
						newList[findIndex] = editedResource;
					}
					if (editedResource.id === this.formControl.value?.id) {
						this.formControl.setValue(editedResource);
					}
					this.resourcesListChange.emit([...newList]);
				}
			});
	}

	private updateResourcesList(){
		this.filteredOptions = this.filterList(this.formControl.value ?? '');
	}

	private addNewResourceButton(resources: ResourceWithValidations[]) {
		const resourcesList = [...resources].filter(r => r.id !== 'addNew');
		if (this.allowResourceCreation) {
			resourcesList.push({
				id: 'addNew',
				name: 'Crear recurso',
				description: '',
				resourceReservations: false,
				inventaries: false,
				projectResources: false,
			});
		}
		return resourcesList;
	}

	private filterList(value: string | ResourceWithValidations): ResourceWithValidations[] {
		let filterValue = '';
		if (typeof value === 'string') {
			filterValue = value.toLowerCase();
		} else {
			filterValue = (<ResourceWithValidations>value).name.toLowerCase();
		}
		const filtered = [...this.resourcesList].filter(option => option.name.toLowerCase().includes(filterValue));
		filtered.sort((a: ResourceWithValidations, b: ResourceWithValidations) => a.name.localeCompare(b.name));

		return this.addNewResourceButton(filtered);
	}
}
